import { WidgetType } from '../../../../shared/constants/widget';
import { WidgetRegistryEntry, WidgetRegistryEntryReturns } from '../types';
import { HEADER_IMPLEMENTATION_OWNERSHIP } from '../constants';
import { resolveWidgetVersion } from '../utils';
import FlashCard from '../../FlashCard';
import BarChart from '../../BarChart';
import BarChartHorizontalRich from '../../OrgDim/BarChart/BarChartHorizontalRich';
import Table from '../../Table';
import FeedbackBanner from '../../FeedbackBanner';
import IconCards from '../../IconCard';
import BenchmarkScoreCard from '../../BenchmarkScoreCard';
import ScoreInfoCard from '../../ScoreInfoCard';
import MultiLevelBarChart from '../../MultiLevelBarChart';
import HeroDimension from '../../OrgDim/HeroDimension';
import DimensionScoreCard from '../../OrgDim/DimensionScoreCard';
import BenchmarkScore from '../../OrgDim/BenchmarkScore';
import RecommendationsCard from '../../OrgDim/DimensionRecommendations';
import BarChartWithTrend from '../../OrgDim/BarChart/BarChartCurve';
import BenchmarkLineChart from '../../IndustryStandards/components/LineChart';
import BenchmarkProgressBar from '../../IndustryStandards/components/IndustryStandardProgressBar';
import PieChart from '../../../components/PieChart';
import PieChartCluster from '../../OrgDim/DimensionQuestionBreakdown';

const WidgetsRegistryMap: Record<WidgetType, WidgetRegistryEntry> = {
  [WidgetType.FLASH_CARD]: {
    component: {
      default: FlashCard,
    },
  },
  [WidgetType.BAR_CHART]: {
    component: {
      default: BarChart,
    },
  },
  [WidgetType.STACKED_BAR_CHART]: {
    component: {
      default: BarChart,
      v2: BarChartHorizontalRich,
    },
  },
  [WidgetType.TABLE]: {
    component: {
      default: Table,
    },
  },
  [WidgetType.FEEDBACK_BANNER]: {
    component: {
      default: FeedbackBanner,
    },
  },
  [WidgetType.ICON_CARDS]: {
    component: {
      default: IconCards,
    },
  },
  [WidgetType.HERO_DIMENSION]: {
    component: {
      default: HeroDimension,
    },
  },
  [WidgetType.DIMENSION_SCORE_CARD]: {
    component: {
      default: DimensionScoreCard,
    },
    headerImplementationOwnership: HEADER_IMPLEMENTATION_OWNERSHIP.Self,
  },
  [WidgetType.BENCHMARK_SCORE_CARD]: {
    component: {
      default: BenchmarkScoreCard,
      v2: BenchmarkScore,
    },
    headerImplementationOwnership: HEADER_IMPLEMENTATION_OWNERSHIP.Self,
  },
  [WidgetType.SCORE_INFO_CARD]: {
    component: {
      default: ScoreInfoCard,
    },
  },
  [WidgetType.MULTI_LEVEL_BAR_CHART]: {
    component: {
      default: MultiLevelBarChart,
    },
  },
  [WidgetType.RECOMMENDATIONS_CARD]: {
    component: {
      default: RecommendationsCard,
    },
    headerImplementationOwnership: HEADER_IMPLEMENTATION_OWNERSHIP.Self,
  },
  [WidgetType.BAR_CHART_WITH_TREND]: {
    component: {
      default: BarChartWithTrend,
    },
  },
  [WidgetType.PIE_CHART]: {
    component: {
      default: PieChart,
    },
  },
  [WidgetType.PIE_CHART_CLUSTER]: {
    component: {
      default: PieChartCluster,
    },
  },
  [WidgetType.BENCHMARK_LINE_CHART]: {
    component: {
      default: BenchmarkLineChart,
    },
  },
  [WidgetType.BENCHMARK_PROGRESS_BAR]: {
    component: {
      default: BenchmarkProgressBar,
    },
  },
};

type Props = {
  widgetType: WidgetType;
  version?: number;
  isGroupedByTabView?: boolean;
};

export default function useWidgetRegistry({
  widgetType,
  version,
  isGroupedByTabView = false,
}: Props): WidgetRegistryEntryReturns {
  const registryEntry = WidgetsRegistryMap[widgetType];

  if (!registryEntry) {
    return {
      component: () => null,
    };
  }

  const versionToLoad = resolveWidgetVersion({
    version,
    isGroupedByTabView,
  });

  const { component: versionedComponents, headerImplementationOwnership } =
    registryEntry;

  return {
    component:
      versionedComponents[versionToLoad] || versionedComponents.default,
    headerImplementationOwnership:
      headerImplementationOwnership || HEADER_IMPLEMENTATION_OWNERSHIP.Wrapper,
  };
}
